import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { TRPC } from '../../contexts/TRPC';
import type { KybStatusData } from '../../lib/verificationStatus';
import { captureAndReportError } from '../../utils/errorReporting';

export interface UseKybStatusRefreshArgs {
  trpc: TRPC['client'];
  activePublisherJoinId: string | undefined;
  hasActivePublisher: boolean;
  isPublisherLoading: boolean;
}

export interface UseKybStatusRefreshResult {
  kybStatus: KybStatusData | null;
  setKybStatus: Dispatch<SetStateAction<KybStatusData | null>>;
  statusLoading: boolean;
  refreshKybStatus: () => Promise<void>;
}

export function useKybStatusRefresh({
  trpc,
  activePublisherJoinId,
  hasActivePublisher,
  isPublisherLoading,
}: UseKybStatusRefreshArgs): UseKybStatusRefreshResult {
  const [kybStatus, setKybStatus] = useState<KybStatusData | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const kybStatusRequestIdRef = useRef(0);

  const refreshKybStatus = async () => {
    const requestId = kybStatusRequestIdRef.current + 1;
    kybStatusRequestIdRef.current = requestId;
    setStatusLoading(true);

    try {
      const result = await trpc.getKybStatus.query(undefined);
      if (
        requestId === kybStatusRequestIdRef.current &&
        '_tag' in result &&
        result._tag === 'Right'
      ) {
        setKybStatus(result.right);
      }
    } catch (err) {
      captureAndReportError('Failed to refresh KYB status:', err);
    } finally {
      if (requestId === kybStatusRequestIdRef.current) {
        setStatusLoading(false);
      }
    }
  };

  useEffect(() => {
    kybStatusRequestIdRef.current += 1;
    setKybStatus(null);

    if (isPublisherLoading) {
      setStatusLoading(true);
      return;
    }

    if (!hasActivePublisher) {
      setStatusLoading(false);
      return;
    }

    void refreshKybStatus();
  }, [activePublisherJoinId, isPublisherLoading, trpc]);

  return {
    kybStatus,
    setKybStatus,
    statusLoading,
    refreshKybStatus,
  };
}
